import {
  CHANNEL_TARGET_STATUS_LABELS,
  COMPANY_LABELS,
  CONTENT_FORMAT_GROUP_LABELS,
  CONTENT_FORMAT_GROUPS,
  CONTENT_FORMAT_LABELS,
  CONTENT_STATUS_LABELS,
  PILLARS,
  PILLAR_LABELS,
  canTransition,
  formatDate,
  formatDateTime,
  isEventFormat,
  type AnnualWheelThemeDto,
  type ContentAssetDto,
  type ContentCollisionDto,
  type ContentFormat,
  type ContentFormatGroup,
  type ContentItemDto,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  CalendarClock,
  CalendarRange,
  Check,
  CheckCircle2,
  Cloud,
  Download,
  ExternalLink,
  History,
  Image as ImageIcon,
  Layers,
  Lightbulb,
  Loader2,
  MessageSquare,
  Plus,
  RotateCcw,
  Send,
  SlidersHorizontal,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Upload,
  UserPlus,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type MouseEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../components/Button.js';
import { Modal } from '../../components/Modal.js';
import { EmptyState } from '../../components/EmptyState.js';
import { MarkdownView, RichTextEditor } from '../../components/RichTextEditor.js';
import { AgentMark, MetaChip, StatusBadge } from '../../components/StatusBadge.js';
import { api, query, type ApiError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import {
  useAddAsset,
  useAddComment,
  useAddContributor,
  useApproveContent,
  useAssets,
  useCancelContent,
  useChannels,
  useCollisions,
  useComments,
  useConfirmPublished,
  useContentItem,
  useContentVersions,
  useDeleteAsset,
  useDeleteContent,
  useRejectContent,
  useRemoveContributor,
  useReopenContent,
  useRevertPublished,
  useScheduleContent,
  useSuggestChannelVariants,
  useSuggestTitle,
  useUpdateChannelTarget,
  useUpdateContent,
  useUploadAsset,
  useUsers,
  useWriteDraft,
} from './useContent.js';
import type { ActivityDto, ContentCommentDto } from '@nova/shared';

/**
 * The right-hand column has two tabs. «Detaljer» stacks the levers, the channels
 * and the graphics in one scroll (nothing hidden behind a sub-tab); «Kommentarer»
 * is the timeline that can mix in the activity log.
 */
type SideTab = 'details' | 'comments';

const SIDE_TABS: { key: SideTab; label: string; Icon: typeof Layers }[] = [
  { key: 'details', label: 'Detaljer', Icon: SlidersHorizontal },
  { key: 'comments', label: 'Kommentarer', Icon: MessageSquare },
];

export function ContentDrawer({
  contentId,
  onClose,
  notice,
}: {
  contentId: string | null;
  onClose: () => void;
  /** Set when the card was opened to explain a blocked move; shown in-content. */
  notice?: string | null;
}) {
  const { data: item, isLoading } = useContentItem(contentId);
  const [side, setSide] = useState<SideTab>('details');

  // A newly opened card always shows the levers next to the draft.
  useEffect(() => setSide('details'), [contentId]);

  return (
    <Modal
      open={Boolean(contentId)}
      onClose={onClose}
      title={item?.title ?? (isLoading ? 'Laster …' : 'Innhold')}
      subtitle={
        item && (
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={item.status} overdue={item.isOverdue} size="sm" />
            {item.area && <MetaChip color={item.area.color}>{item.area.name}</MetaChip>}
            <MetaChip>{PILLAR_LABELS[item.pillar]}</MetaChip>
            <MetaChip>{CONTENT_FORMAT_LABELS[item.format]}</MetaChip>
            {item.source === 'AGENT' && <AgentMark />}
          </div>
        )
      }
      footer={item && <ActionBar item={item} onClose={onClose} />}
    >
      {item && (
        <div className="space-y-6">
          {/* The wider modal lets the work spread out: the draft on the left, its
              levers and side threads on the right, and the log tucked away at the
              bottom. On a narrow screen it all stacks into one column. */}
          <div className="grid gap-x-6 gap-y-6 lg:grid-cols-[minmax(0,1fr)_21rem] xl:grid-cols-[minmax(0,1fr)_23rem]">
            {/* Left: the draft itself. It is always mounted, so a half-written body
                survives switching the side panel or scrolling away — not lost the
                way it would be if the editor unmounted. */}
            <div className="min-w-0">
              <ContentTab item={item} notice={notice} />
            </div>

            {/* Right: the owner's levers (Detaljer) and the side threads, in their
                own compact tab set so the column stays calm rather than a long
                stack. */}
            <div className="border-hairline min-w-0 lg:border-l lg:pl-6">
              <nav className="border-hairline mb-4 flex gap-0.5 border-b">
                {SIDE_TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSide(key)}
                    className={cn(
                      'relative inline-flex items-center gap-1.5 px-2.5 py-2 text-xs transition',
                      side === key ? 'text-accent' : 'text-ink-soft hover:text-ink',
                    )}
                  >
                    <Icon size={13} aria-hidden />
                    {label}
                    {side === key && (
                      <span
                        aria-hidden
                        className="bg-accent absolute inset-x-2 -bottom-px h-0.5 rounded-full"
                      />
                    )}
                  </button>
                ))}
              </nav>

              {side === 'details' && (
                <div className="space-y-6">
                  {/* Detaljer, Kanaler og Grafikk er alle åpne samtidig — ingen
                      under-faner, alt synlig i én kolonne. */}
                  <section>
                    <p className="label-micro mb-3 inline-flex items-center gap-1.5">
                      <SlidersHorizontal size={12} aria-hidden />
                      Detaljer
                    </p>
                    <DetailsPanel item={item} />
                  </section>
                  <section className="border-hairline border-t pt-5">
                    <p className="label-micro mb-3 inline-flex items-center gap-1.5">
                      <Layers size={12} aria-hidden />
                      Kanaler
                    </p>
                    <ChannelsTab item={item} />
                  </section>
                  <section className="border-hairline border-t pt-5">
                    <p className="label-micro mb-3 inline-flex items-center gap-1.5">
                      <ImageIcon size={12} aria-hidden />
                      Grafikk
                    </p>
                    <GraphicsTab item={item} />
                  </section>
                </div>
              )}
              {side === 'comments' && <CommentsTab item={item} />}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Innhold ─────────────────────────────────────────────────────────────────

function ContentTab({ item, notice }: { item: ContentItemDto; notice?: string | null }) {
  const update = useUpdateContent(item.id);
  const suggestTitle = useSuggestTitle(item.id);
  const [title, setTitle] = useState(item.title);
  const [body, setBody] = useState(item.body ?? '');
  const [summary, setSummary] = useState(item.summary ?? '');
  const [titleSuggestions, setTitleSuggestions] = useState<string[]>([]);
  const locked = item.status === 'PUBLISHED';

  // A too-short title is not saved (the API rejects < 3 chars); the buffer keeps
  // it so the user can fix it, and a hint explains why it is not saving.
  const titleValid = title.trim().length >= 3;

  // What the server holds. Autosave keeps this in step; we compare against it to
  // know what is still unsaved, and to adopt text that arrives from elsewhere
  // (Nova writing a draft, another user) without clobbering local edits.
  const saved = useRef({ title: item.title, body: item.body ?? '', summary: item.summary ?? '' });

  // Another card opened — reset the buffer to the new item.
  useEffect(() => {
    setTitle(item.title);
    setBody(item.body ?? '');
    setSummary(item.summary ?? '');
    saved.current = { title: item.title, body: item.body ?? '', summary: item.summary ?? '' };
  }, [item.id]);

  // The server text changed under us — adopt it, but only where the user has no
  // unsaved edit of their own that adopting would overwrite.
  useEffect(() => {
    if (item.title !== saved.current.title && title === saved.current.title) {
      setTitle(item.title);
      saved.current.title = item.title;
    }
    const serverBody = item.body ?? '';
    if (serverBody !== saved.current.body && body === saved.current.body) {
      setBody(serverBody);
      saved.current.body = serverBody;
    }
    const serverSummary = item.summary ?? '';
    if (serverSummary !== saved.current.summary && summary === saved.current.summary) {
      setSummary(serverSummary);
      saved.current.summary = serverSummary;
    }
  }, [item.title, item.body, item.summary, title, body, summary]);

  const titleDirty = titleValid && title !== saved.current.title;
  const dirty = titleDirty || body !== saved.current.body || summary !== saved.current.summary;

  // Autosave a short moment after typing stops, so switching tabs or closing the
  // drawer never loses work and nothing has to be saved by hand.
  useEffect(() => {
    if (locked || !dirty) return;
    const handle = setTimeout(() => {
      update.mutate(
        { ...(titleDirty ? { title } : {}), body, summary, autosave: true },
        {
          onSuccess: () =>
            (saved.current = { title: titleDirty ? title : saved.current.title, body, summary }),
        },
      );
    }, 1200);
    return () => clearTimeout(handle);
    // update.mutate is stable; omitting it keeps the debounce from resetting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, summary, dirty, titleDirty, locked]);

  // Closing the drawer unmounts this tab and cancels the pending debounce, so
  // flush the last unsaved edits on the way out. `latest` keeps the closure fresh.
  const latest = useRef({ title, body, summary, dirty, titleDirty, locked });
  latest.current = { title, body, summary, dirty, titleDirty, locked };
  useEffect(() => {
    return () => {
      const { title: t, body: b, summary: s, dirty: d, titleDirty: td, locked: l } = latest.current;
      if (d && !l)
        update.mutate({ ...(td ? { title: t } : {}), body: b, summary: s, autosave: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The brief grows to fit what is written, so nothing is hidden behind a scroll.
  const summaryRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = summaryRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [summary]);

  return (
    <div className="space-y-5">
      {notice && (
        <section className="border-warning/30 bg-warning/[0.09] rounded-lg border p-3.5">
          <p className="label-micro text-warning mb-1">Ikke klart ennå</p>
          <p className="text-ink-soft text-sm leading-relaxed">{notice}</p>
        </section>
      )}

      {item.agentRationale && (
        <section className="border-violet/20 bg-violet/[0.07] rounded-lg border p-3.5">
          <p className="label-micro text-violet mb-1.5">Derfor foreslår Nova dette</p>
          {/* Markdown: the planner argues in sections — hvorfor nå, for hvem,
              kanalvalg — with source links (07 — 2.2.2). Older one-line
              rationales render as a plain paragraph. */}
          <MarkdownView markdown={item.agentRationale} />
        </section>
      )}

      {item.rejectedReason && (
        <section className="border-danger/25 bg-danger/[0.07] rounded-lg border p-3.5">
          <p className="label-micro text-danger mb-1.5">Begrunnelse for avvisning</p>
          <p className="text-ink-soft text-sm leading-relaxed">{item.rejectedReason}</p>
        </section>
      )}

      <CollisionBanner item={item} />

      <section>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <label htmlFor="content-title" className="label-micro">
            Tittel
          </label>
          {!locked && (
            <button
              type="button"
              onClick={() =>
                suggestTitle.mutate(undefined, {
                  onSuccess: (data) => setTitleSuggestions(data.suggestions),
                })
              }
              disabled={suggestTitle.isPending}
              className="text-accent hover:text-accent/80 inline-flex items-center gap-1 text-[11px] transition disabled:opacity-60"
            >
              {suggestTitle.isPending ? (
                <Loader2 size={12} className="animate-spin" aria-hidden />
              ) : (
                <Lightbulb size={12} aria-hidden />
              )}
              {suggestTitle.isPending ? 'Nova tenker …' : 'Foreslå tittel'}
            </button>
          )}
        </div>
        <input
          id="content-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          readOnly={locked}
          placeholder="Tittel på saken"
          className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 w-full rounded-lg border p-3 text-sm font-medium"
        />
        {!titleValid && (
          <p className="text-warning mt-1.5 text-[11px]">
            Tittelen må ha minst 3 tegn for å lagres.
          </p>
        )}
        {suggestTitle.isError && (
          <p className="text-danger mt-1.5 text-[11px]">{(suggestTitle.error as Error).message}</p>
        )}
        {titleSuggestions.length > 0 && (
          <div className="border-accent/20 bg-accent/[0.05] mt-2 space-y-1.5 rounded-lg border p-2.5">
            <p className="label-micro text-accent">Novas forslag — klikk for å bruke</p>
            {titleSuggestions.map((suggestion, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setTitle(suggestion);
                  setTitleSuggestions([]);
                }}
                className="border-hairline bg-base/40 text-ink hover:border-accent/40 block w-full rounded-lg border px-3 py-2 text-left text-sm transition"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </section>

      <section>
        <label htmlFor="summary" className="label-micro mb-1.5 block">
          Kort beskrivelse / brief
        </label>
        <textarea
          ref={summaryRef}
          id="summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          readOnly={locked}
          rows={2}
          style={{ minHeight: '4.5rem' }}
          className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 w-full resize-none overflow-hidden rounded-lg border p-3 text-sm"
          placeholder="Hva skal denne saken handle om?"
        />
      </section>

      <section>
        <div className="mb-1.5 flex items-center justify-between">
          <span className="label-micro">Innhold</span>
          <VersionHistoryButton item={item} />
        </div>
        <RichTextEditor
          value={body}
          onChange={setBody}
          readOnly={locked}
          autoFocus={Boolean(notice)}
        />
        <AiEditBadge item={item} />
      </section>

      {!locked && (
        <div className="text-ink-soft flex items-center gap-2 text-xs">
          <SaveStatus pending={update.isPending} dirty={dirty} />
          {update.isError && <span className="text-danger">{(update.error as Error).message}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * The owner's levers and context — what used to hide under «Detaljer» in the
 * side drawer. In the wider modal it gets its own column next to the draft:
 * pilar, format, datoer, ansvarlig, deltakere.
 */
function DetailsPanel({ item }: { item: ContentItemDto }) {
  return (
    <div className="space-y-5">
      <EditableMeta item={item} />
      <ContributorsField item={item} />
      <PublishMonthContext publishAt={item.publishAt} />
    </div>
  );
}

/** «X dager før/etter» / «samme dag», from a signed day offset (other − this). */
function collisionTiming(dayOffset: number): string {
  if (dayOffset === 0) return 'samme dag';
  const n = Math.abs(dayOffset);
  const unit = n === 1 ? 'dag' : 'dager';
  return dayOffset < 0 ? `${n} ${unit} før` : `${n} ${unit} etter`;
}

function collisionText(c: ContentCollisionDto): string {
  const who = COMPANY_LABELS[c.company];
  const when = collisionTiming(c.dayOffset);
  if (c.kind === 'EVENT') {
    return `${who} har allerede ${CONTENT_FORMAT_LABELS[c.format].toLowerCase()} «${c.title}» ${when} (${formatDate(c.date)}).`;
  }
  return `${who} publiserer «${c.title}» ${when} (${formatDate(c.date)}).`;
}

/**
 * A soft, company-level heads-up (±3 dager): other events or publications close
 * to this one's date. Never blocks — it only informs, so the owner can move a
 * date if two things clash the same week.
 */
function CollisionBanner({ item }: { item: ContentItemDto }) {
  const { data } = useCollisions(item);
  const collisions = data?.collisions ?? [];
  if (collisions.length === 0) return null;

  return (
    <section className="border-warning/30 bg-warning/[0.09] rounded-lg border p-3.5">
      <p className="label-micro text-warning mb-1.5 inline-flex items-center gap-1.5">
        <AlertTriangle size={12} aria-hidden />
        Obs: noe annet skjer samtidig
      </p>
      <ul className="text-ink-soft space-y-1 text-sm leading-relaxed">
        {collisions.map((c) => (
          <li key={`${c.id}-${c.kind}`}>{collisionText(c)}</li>
        ))}
      </ul>
      <p className="text-ink-soft/70 mt-2 text-[11px]">
        Dette stopper ingenting — vurder om datoen bør flyttes for å unngå å konkurrere om
        oppmerksomheten.
      </p>
    </section>
  );
}

/**
 * How much the human has reworked Nova's draft. Below ~15 % changed, scheduling
 * is held back (kvalitetssikring) — the badge warns before you get there.
 */
function AiEditBadge({ item }: { item: ContentItemDto }) {
  const { data } = useQuery({
    queryKey: ['ai-similarity', item.id, item.body ?? ''],
    queryFn: () =>
      api.get<{ basedOnAiDraft: boolean; changedRatio: number | null }>(
        `/content/${item.id}/ai-similarity`,
      ),
    enabled: Boolean(item.body?.trim()),
  });

  if (!data?.basedOnAiDraft || data.changedRatio === null) return null;
  const pct = Math.round(data.changedRatio * 100);
  const low = data.changedRatio < 0.15;

  return (
    <p
      className={cn(
        'mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px]',
        low
          ? 'border-warning/30 bg-warning/10 text-warning'
          : 'border-hairline bg-base/30 text-ink-soft',
      )}
    >
      <Sparkles size={11} aria-hidden />
      Bearbeidet fra Novas utkast: {pct} %{low && ' — skriv mer om før publisering'}
    </p>
  );
}

/** The quiet autosave indicator that replaces a manual save button. */
function SaveStatus({ pending, dirty }: { pending: boolean; dirty: boolean }) {
  if (pending) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Loader2 size={13} className="animate-spin" aria-hidden />
        Lagrer …
      </span>
    );
  }
  if (dirty) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Cloud size={13} aria-hidden />
        Lagres automatisk …
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <CheckCircle2 size={13} className="text-accent" aria-hidden />
      Alt er lagret
    </span>
  );
}

/**
 * Explains the annual-wheel theme where the date is actually chosen (07 —
 * 2.2.2) — the stripe on the timeline says that a theme exists; this says what
 * it means for this item.
 */
function PublishMonthContext({ publishAt }: { publishAt: string | null }) {
  const { data: wheel = [] } = useQuery({
    queryKey: ['annual-wheel'],
    queryFn: () => api.get<AnnualWheelThemeDto[]>('/dashboard/annual-wheel'),
    staleTime: 60 * 60_000,
  });

  if (!publishAt) return null;
  const date = new Date(publishAt);
  const theme = wheel.find((t) => t.year === date.getFullYear() && t.month === date.getMonth() + 1);
  if (!theme) return null;

  const month = date.toLocaleDateString('nb-NO', { month: 'long' });

  return (
    <p className="border-hairline bg-base/30 text-ink-soft rounded-lg border px-3 py-2 text-[12px] leading-relaxed">
      <CalendarRange size={12} className="text-accent mr-1.5 inline align-[-1px]" aria-hidden />
      Publiseringen ligger i {month}, der årshjultemaet er «{theme.theme}»
      {theme.notes ? ` — ${theme.notes}` : '.'}
    </p>
  );
}

/**
 * Draft deadline, publish date and owner stay editable through the whole flow
 * (they are the levers a product owner keeps adjusting) — only published content
 * freezes. Each change saves on its own.
 */
function EditableMeta({ item }: { item: ContentItemDto }) {
  const update = useUpdateContent(item.id);
  const { data: users = [] } = useUsers();
  const locked = item.status === 'PUBLISHED';

  // `color-scheme` is left to the active theme (set per palette in styles.css) so
  // the native date popups are legible in light, pastell and dark alike.
  const inputClass =
    'text-ink w-full cursor-pointer bg-transparent text-sm outline-none disabled:cursor-default disabled:opacity-60';

  // Clicking anywhere on a date field opens the native picker, not just the icon.
  const openPicker = (e: MouseEvent<HTMLInputElement>) => {
    try {
      e.currentTarget.showPicker?.();
    } catch {
      /* already open or unsupported — the icon still works */
    }
  };

  return (
    // Keyed on the item so the uncontrolled inputs reset when another card opens.
    <section key={item.id} className="grid grid-cols-2 gap-3">
      <label className="border-hairline bg-base/30 flex flex-col gap-1 rounded-lg border px-3 py-2">
        <span className="label-micro">Innholdspilar</span>
        <select
          disabled={locked}
          defaultValue={item.pillar}
          onChange={(e) => update.mutate({ pillar: e.target.value })}
          className={inputClass}
        >
          {PILLARS.map((p) => (
            <option key={p} value={p}>
              {PILLAR_LABELS[p]}
            </option>
          ))}
        </select>
      </label>

      <label className="border-hairline bg-base/30 flex flex-col gap-1 rounded-lg border px-3 py-2">
        <span className="label-micro">Format</span>
        <select
          disabled={locked}
          defaultValue={item.format}
          onChange={(e) => update.mutate({ format: e.target.value as ContentFormat })}
          className={inputClass}
        >
          {(Object.keys(CONTENT_FORMAT_GROUPS) as ContentFormatGroup[]).map((group) => (
            <optgroup key={group} label={CONTENT_FORMAT_GROUP_LABELS[group]}>
              {CONTENT_FORMAT_GROUPS[group].map((option) => (
                <option key={option} value={option}>
                  {CONTENT_FORMAT_LABELS[option]}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      <label className="border-hairline bg-base/30 flex flex-col gap-1 rounded-lg border px-3 py-2">
        <span className="label-micro">Frist for utkast</span>
        <input
          type="date"
          disabled={locked}
          defaultValue={toDateInput(item.draftDeadline)}
          onClick={openPicker}
          onChange={(e) =>
            update.mutate({
              draftDeadline: e.target.value ? new Date(e.target.value).toISOString() : null,
            })
          }
          className={inputClass}
        />
      </label>

      <label className="border-hairline bg-base/30 flex flex-col gap-1 rounded-lg border px-3 py-2">
        <span className="label-micro">Planlagt publisering</span>
        <input
          type="datetime-local"
          disabled={locked}
          defaultValue={item.publishAt ? toLocalInput(item.publishAt) : ''}
          onClick={openPicker}
          onChange={(e) =>
            update.mutate({
              publishAt: e.target.value ? new Date(e.target.value).toISOString() : null,
            })
          }
          className={inputClass}
        />
      </label>

      {isEventFormat(item.format) && (
        <label className="border-accent/25 bg-accent/[0.05] col-span-2 flex flex-col gap-1 rounded-lg border px-3 py-2">
          <span className="label-micro inline-flex items-center gap-1.5">
            <CalendarClock size={12} className="text-accent" aria-hidden />
            Dato for gjennomføring
          </span>
          <input
            type="datetime-local"
            disabled={locked}
            defaultValue={item.eventDate ? toLocalInput(item.eventDate) : ''}
            onClick={openPicker}
            onChange={(e) =>
              update.mutate({
                eventDate: e.target.value ? new Date(e.target.value).toISOString() : null,
              })
            }
            className={inputClass}
          />
          <span className="text-ink-soft/70 text-[11px]">
            Når {CONTENT_FORMAT_LABELS[item.format].toLowerCase()}et faktisk holdes.
          </span>
        </label>
      )}

      <label className="border-hairline bg-base/30 flex flex-col gap-1 rounded-lg border px-3 py-2">
        <span className="label-micro">Ansvarlig</span>
        <select
          disabled={locked}
          defaultValue={item.ownerUserId ?? ''}
          onChange={(e) => update.mutate({ ownerUserId: e.target.value })}
          className={inputClass}
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </label>

      <Field label="Opprettet" value={item.createdAt} />

      {update.isError && (
        <p className="text-danger col-span-2 text-xs">{(update.error as Error).message}</p>
      )}
    </section>
  );
}

function Field({ label, value, text }: { label: string; value?: string | null; text?: string }) {
  return (
    <div className="border-hairline bg-base/30 rounded-lg border px-3 py-2">
      <p className="label-micro">{label}</p>
      <p className="text-ink mt-1 text-sm">
        {text ?? (value ? formatDateTime(value) : 'Ikke satt')}
      </p>
    </div>
  );
}

/**
 * The people on a post besides the owner. They are notified about comments, not
 * about deadlines — that stays with the responsible person (ansvarlig).
 */
function ContributorsField({ item }: { item: ContentItemDto }) {
  const { data: users = [] } = useUsers();
  const addContributor = useAddContributor(item.id);
  const removeContributor = useRemoveContributor(item.id);
  const locked = item.status === 'PUBLISHED';

  const contributors = item.contributors ?? [];
  // The owner is already responsible; don't offer them as a contributor too.
  const takenIds = new Set([item.ownerUserId, ...contributors.map((c) => c.id)].filter(Boolean));
  const addable = users.filter((u) => u.isActive && !takenIds.has(u.id));

  return (
    <section className="border-hairline bg-base/30 rounded-lg border px-3 py-2.5">
      <div className="mb-2 flex items-center justify-between">
        <span className="label-micro">Deltakere</span>
        <span className="text-ink-soft text-[11px]">Varsles om kommentarer</span>
      </div>

      {contributors.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {contributors.map((c) => (
            <span
              key={c.id}
              className="border-hairline bg-raised/60 text-ink inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px]"
            >
              {c.name}
              {!locked && (
                <button
                  type="button"
                  aria-label={`Fjern ${c.name}`}
                  onClick={() => removeContributor.mutate(c.id)}
                  className="text-ink-soft hover:text-danger transition"
                >
                  <X size={12} aria-hidden />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-ink-soft mb-2 text-xs">Ingen deltakere lagt til ennå.</p>
      )}

      {!locked && addable.length > 0 && (
        <div className="relative">
          <UserPlus
            size={13}
            className="text-ink-soft pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
            aria-hidden
          />
          <select
            aria-label="Legg til deltaker"
            value=""
            disabled={addContributor.isPending}
            onChange={(e) => e.target.value && addContributor.mutate(e.target.value)}
            className="border-hairline bg-base/40 text-ink-soft h-9 w-full rounded-lg border pl-8 pr-3 text-xs"
          >
            <option value="">Legg til deltaker …</option>
            {addable.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {(addContributor.isError || removeContributor.isError) && (
        <p className="text-danger mt-1.5 text-xs">
          {((addContributor.error ?? removeContributor.error) as Error).message}
        </p>
      )}
    </section>
  );
}

// ── Kommentarer ─────────────────────────────────────────────────────────────

/** One strand of the combined thread: an authored comment or a system event. */
type ThreadEntry =
  | { kind: 'comment'; id: string; at: number; comment: ContentCommentDto }
  | { kind: 'activity'; id: string; at: number; activity: ActivityDto };

/**
 * The internal thread (intern forklaring) as a single chronological timeline.
 * Comments are always shown; «Vis hendelseslogg» folds the activity-log events
 * into the same stream, time-ordered and visually set apart as muted system
 * entries. The composer stays at the bottom.
 */
function CommentsTab({ item }: { item: ContentItemDto }) {
  const { user } = useAuth();
  const { data: comments = [], isLoading } = useComments(item.id);
  const addComment = useAddComment(item.id);
  const [draft, setDraft] = useState('');
  const [showActivity, setShowActivity] = useState(false);

  // The log is fetched only once the toggle is on — it is not needed otherwise.
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['activity', { entityId: item.id }],
    queryFn: () =>
      api.get<{ items: ActivityDto[] }>(
        `/activity${query({ entityType: 'content', entityId: item.id, take: 50 })}`,
      ),
    enabled: showActivity,
  });

  function submit() {
    const body = draft.trim();
    if (!body) return;
    addComment.mutate(body, { onSuccess: () => setDraft('') });
  }

  // Oldest first, so the newest sits just above the composer — chat convention.
  const entries: ThreadEntry[] = [
    ...comments.map((c) => ({
      kind: 'comment' as const,
      id: `c-${c.id}`,
      at: new Date(c.createdAt).getTime(),
      comment: c,
    })),
    ...(showActivity
      ? (activityData?.items ?? []).map((a) => ({
          kind: 'activity' as const,
          id: `a-${a.id}`,
          at: new Date(a.createdAt).getTime(),
          activity: a,
        }))
      : []),
  ].sort((a, b) => a.at - b.at);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-ink-soft text-xs leading-relaxed">
          Intern tråd — synlig for ansvarlig og deltakere, ikke i det som publiseres. Alle på saken
          varsles om nye kommentarer.
        </p>
        <button
          type="button"
          onClick={() => setShowActivity((v) => !v)}
          aria-pressed={showActivity}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition',
            showActivity
              ? 'border-accent/40 bg-accent/15 text-accent'
              : 'border-hairline bg-raised/50 text-ink-soft hover:text-ink',
          )}
        >
          <History size={12} aria-hidden />
          Vis hendelseslogg
        </button>
      </div>

      {isLoading || (showActivity && activityLoading && !activityData) ? (
        <p className="text-ink-soft text-sm">Henter tråden …</p>
      ) : entries.length === 0 ? (
        <EmptyState
          title="Ingen kommentarer ennå"
          description="Skriv den første forklaringen om hvor saken står."
        />
      ) : (
        <ol className="border-hairline space-y-3 border-l pl-4">
          {entries.map((entry) =>
            entry.kind === 'comment' ? (
              <ThreadComment key={entry.id} comment={entry.comment} user={user} />
            ) : (
              <ThreadActivity key={entry.id} activity={entry.activity} />
            ),
          )}
        </ol>
      )}

      <div className="border-hairline border-t pt-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // ⌘/Ctrl+Enter sends, like most comment fields.
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
          }}
          rows={3}
          placeholder="Skriv en intern kommentar …"
          className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 w-full resize-y rounded-lg border p-3 text-sm"
        />
        <div className="mt-2 flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            icon={<Send size={14} aria-hidden />}
            disabled={!draft.trim()}
            loading={addComment.isPending}
            onClick={submit}
          >
            Kommentér
          </Button>
          <span className="text-ink-soft/70 text-[11px]">⌘/Ctrl + Enter</span>
          {addComment.isError && (
            <span className="text-danger text-xs">{(addComment.error as Error).message}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** An authored comment on the combined thread — a card with its own dot. */
function ThreadComment({
  comment,
  user,
}: {
  comment: ContentCommentDto;
  user: { id: string } | null | undefined;
}) {
  const mine = Boolean(user?.id) && comment.authorId === user?.id;
  return (
    <li className="relative">
      <span
        aria-hidden
        className="bg-accent absolute -left-[21px] top-1.5 h-2 w-2 rounded-full ring-2 ring-[rgb(var(--nova-panel))]"
      />
      <div
        className={cn(
          'rounded-lg border p-3',
          mine ? 'border-accent/30 bg-accent/[0.10]' : 'border-hairline bg-base/30',
        )}
      >
        <div className="mb-1 flex items-center gap-2">
          <span className="text-ink text-sm font-medium">{mine ? 'Deg' : comment.authorName}</span>
          <span className="text-ink-soft/70 text-[11px]">{formatDateTime(comment.createdAt)}</span>
        </div>
        <p className="text-ink-soft whitespace-pre-wrap text-sm leading-relaxed">{comment.body}</p>
      </div>
    </li>
  );
}

/** A muted system event folded into the thread — set apart from comments. */
function ThreadActivity({ activity }: { activity: ActivityDto }) {
  return (
    <li className="relative">
      <span
        aria-hidden
        className={cn(
          'absolute -left-[21px] top-1.5 h-2 w-2 rounded-full',
          activity.actorType === 'AGENT' ? 'bg-violet' : 'bg-ink-soft/50',
        )}
      />
      <p className="text-ink-soft/80 text-[13px] leading-snug">{activity.message}</p>
      <p className="text-ink-soft/60 mt-0.5 text-[11px]">{formatDateTime(activity.createdAt)}</p>
    </li>
  );
}

// ── Bilde/grafikk ─────────────────────────────────────────────────────────────

/** Graphics orders on the card: a brief, and the finished file once uploaded. */
function GraphicsTab({ item }: { item: ContentItemDto }) {
  const { data: assets = [], isLoading } = useAssets(item.id);
  const addAsset = useAddAsset(item.id);
  const [brief, setBrief] = useState('');

  function submit() {
    const value = brief.trim();
    if (!value) return;
    addAsset.mutate(value, { onSuccess: () => setBrief('') });
  }

  return (
    <div className="space-y-4">
      <p className="text-ink-soft text-xs leading-relaxed">
        Bestill et bilde eller en grafikk til saken, og last opp den ferdige filen på kortet når den
        er laget.
      </p>

      {isLoading ? (
        <p className="text-ink-soft text-sm">Henter bestillinger …</p>
      ) : assets.length === 0 ? (
        <EmptyState
          title="Ingen grafikk bestilt ennå"
          description="Beskriv hva som trengs, så kan filen lastes opp senere."
        />
      ) : (
        <div className="space-y-3">
          {assets.map((asset) => (
            <AssetCard key={asset.id} item={item} asset={asset} />
          ))}
        </div>
      )}

      <div className="border-hairline border-t pt-3">
        <label htmlFor="new-asset" className="label-micro mb-1.5 block">
          Ny bestilling
        </label>
        <textarea
          id="new-asset"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={2}
          placeholder="F.eks. «Forsidebilde: illustrasjon av en saksbehandler ved skjerm, i profilfargene»"
          className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 w-full resize-y rounded-lg border p-3 text-sm"
        />
        <div className="mt-2 flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            icon={<ImageIcon size={14} aria-hidden />}
            disabled={!brief.trim()}
            loading={addAsset.isPending}
            onClick={submit}
          >
            Bestill grafikk
          </Button>
          {addAsset.isError && (
            <span className="text-danger text-xs">{(addAsset.error as Error).message}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function AssetCard({ item, asset }: { item: ContentItemDto; asset: ContentAssetDto }) {
  const upload = useUploadAsset(item.id);
  const remove = useDeleteAsset(item.id);
  const fileInput = useRef<HTMLInputElement>(null);

  const fileUrl = `/api/v1/content/${item.id}/assets/${asset.id}/file`;
  const isImage = Boolean(asset.mimeType?.startsWith('image/'));

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) upload.mutate({ assetId: asset.id, file });
    e.target.value = '';
  }

  return (
    <section className="border-hairline bg-base/30 rounded-lg border p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-ink whitespace-pre-wrap text-sm leading-relaxed">{asset.brief}</p>
          <p className="text-ink-soft/70 mt-1 text-[11px]">Bestilt av {asset.createdByName}</p>
        </div>
        <button
          type="button"
          aria-label="Fjern bestillingen"
          onClick={() => remove.mutate(asset.id)}
          className="text-ink-soft hover:text-danger shrink-0 transition"
        >
          <Trash2 size={14} aria-hidden />
        </button>
      </div>

      {asset.hasFile && (
        <div className="mt-3">
          {isImage && (
            <a href={fileUrl} target="_blank" rel="noreferrer noopener">
              <img
                src={fileUrl}
                alt={asset.fileName ?? 'Opplastet grafikk'}
                className="border-hairline max-h-64 w-auto rounded-lg border"
              />
            </a>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <a
              href={fileUrl}
              download={asset.fileName ?? undefined}
              className="border-hairline bg-raised/60 text-ink hover:bg-raised inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition"
            >
              <Download size={13} aria-hidden />
              {asset.fileName ?? 'Last ned'}
              {asset.fileSize !== null && (
                <span className="text-ink-soft/70">· {Math.round(asset.fileSize / 1024)} kB</span>
              )}
            </a>
          </div>
        </div>
      )}

      <div className="mt-3">
        <input
          ref={fileInput}
          type="file"
          hidden
          onChange={onPick}
          // Graphics are usually images or PDFs, but a designer may deliver more.
          accept="image/*,application/pdf,.svg,.ai,.eps"
        />
        <Button
          size="sm"
          icon={<Upload size={13} aria-hidden />}
          loading={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {asset.hasFile ? 'Erstatt fil' : 'Last opp fil'}
        </Button>
        {upload.isError && (
          <span className="text-danger ml-2 text-xs">{(upload.error as Error).message}</span>
        )}
      </div>
    </section>
  );
}

// ── Kanaler ─────────────────────────────────────────────────────────────────

function ChannelsTab({ item }: { item: ContentItemDto }) {
  const updateTarget = useUpdateChannelTarget(item.id);
  const confirm = useConfirmPublished(item.id);
  const update = useUpdateContent(item.id);
  const suggestVariants = useSuggestChannelVariants(item.id);
  const { data: channels = [] } = useChannels();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [externalRef, setExternalRef] = useState('');

  const targets = item.channelTargets ?? [];
  const selectedIds = targets.map((t) => t.channelId);
  // A channel that is already confirmed published cannot be taken off the card.
  const publishedIds = new Set(
    targets.filter((t) => t.status === 'PUBLISHED_CONFIRMED').map((t) => t.channelId),
  );
  const locked = item.status === 'PUBLISHED';
  const hasBody = Boolean(item.body?.trim());
  const publishedCount = targets.filter((t) => t.status === 'PUBLISHED_CONFIRMED').length;
  const anyEmpty = targets.some(
    (t) => t.status !== 'PUBLISHED_CONFIRMED' && !t.adaptedBody?.trim(),
  );
  const anyFilled = targets.some((t) => t.adaptedBody?.trim());

  const toggleChannel = (channelId: string) => {
    if (locked || publishedIds.has(channelId)) return;
    const next = selectedIds.includes(channelId)
      ? selectedIds.filter((id) => id !== channelId)
      : [...selectedIds, channelId];
    update.mutate({ channelIds: next });
  };

  return (
    <div className="space-y-3">
      {/* Channel variants: one adapted version per channel, with per-channel
          confirm and publish progress. The item flips to Publisert only once
          every channel is confirmed — that logic lives in the API. */}
      {targets.length > 0 && (
        <section className="border-hairline bg-base/30 rounded-lg border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="label-micro mb-0.5 inline-flex items-center gap-1.5">
                <Layers size={12} aria-hidden />
                Kanalversjoner
              </p>
              <p className="text-ink-soft text-[11px]">
                {targets.length} kanal{targets.length === 1 ? '' : 'er'} · {publishedCount} av{' '}
                {targets.length} publisert
              </p>
            </div>
            {!locked && hasBody && (anyEmpty || anyFilled) && (
              <div className="flex flex-wrap gap-2">
                {anyEmpty && (
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Sparkles size={13} aria-hidden />}
                    loading={suggestVariants.isPending}
                    onClick={() => suggestVariants.mutate(undefined)}
                  >
                    Foreslå kanalversjoner
                  </Button>
                )}
                {anyFilled && (
                  <Button
                    size="sm"
                    icon={<Sparkles size={13} aria-hidden />}
                    loading={suggestVariants.isPending}
                    onClick={() => suggestVariants.mutate({ force: true })}
                  >
                    Skriv om alle
                  </Button>
                )}
              </div>
            )}
          </div>
          {!hasBody && (
            <p className="text-ink-soft/70 mt-2 text-[11px]">
              Skriv et utkast i «Innhold» først — så kan Nova tilpasse det til hver kanal.
            </p>
          )}
          {suggestVariants.isError && (
            <p className="text-danger mt-2 text-xs">{(suggestVariants.error as Error).message}</p>
          )}
        </section>
      )}

      {/* Add or remove channels directly on the card — not only when scheduling.
          A confirmed-published channel stays locked on. */}
      {!locked && (
        <section className="border-hairline bg-base/30 rounded-lg border p-3">
          <p className="label-micro mb-2">Kanaler på saken</p>
          <div className="flex flex-wrap gap-1.5">
            {channels.map((channel) => {
              const active = selectedIds.includes(channel.id);
              const fixed = publishedIds.has(channel.id);
              return (
                <button
                  key={channel.id}
                  type="button"
                  onClick={() => toggleChannel(channel.id)}
                  disabled={fixed || update.isPending}
                  aria-pressed={active}
                  title={fixed ? 'Publisert – kan ikke fjernes' : undefined}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition disabled:opacity-60',
                    active
                      ? 'border-accent/40 bg-accent/15 text-accent'
                      : 'border-hairline bg-raised/50 text-ink-soft hover:text-ink',
                  )}
                >
                  {active ? <Check size={11} aria-hidden /> : <Plus size={11} aria-hidden />}
                  {channel.name}
                </button>
              );
            })}
          </div>
          {update.isError && (
            <p className="text-danger mt-2 text-xs">{(update.error as Error).message}</p>
          )}
        </section>
      )}

      {targets.length === 0 && (
        <EmptyState
          title="Ingen kanaler valgt"
          description="Velg kanaler over, eller når du planlegger publiseringen."
        />
      )}

      {targets.map((target) => (
        <section key={target.id} className="border-hairline bg-base/30 rounded-lg border p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-ink truncate text-sm">{target.channel?.name ?? 'Kanal'}</p>
              <p className="text-ink-soft mt-0.5 text-[11px]">
                {CHANNEL_TARGET_STATUS_LABELS[target.status]}
                {target.publishedAt && ` · ${formatDateTime(target.publishedAt)}`}
              </p>
            </div>
            {target.externalRef && (
              <a
                href={target.externalRef}
                target="_blank"
                rel="noreferrer noopener"
                className="text-accent inline-flex items-center gap-1 text-[11px]"
              >
                Åpne <ExternalLink size={11} aria-hidden />
              </a>
            )}
          </div>

          {editing === target.id ? (
            <div className="mt-3 space-y-2">
              <RichTextEditor value={draft} onChange={setDraft} minHeight={160} />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  loading={updateTarget.isPending}
                  onClick={() =>
                    updateTarget.mutate(
                      { targetId: target.id, adaptedBody: draft, status: 'PREPARED' },
                      { onSuccess: () => setEditing(null) },
                    )
                  }
                >
                  Lagre kanalversjon
                </Button>
                <Button size="sm" onClick={() => setEditing(null)}>
                  Avbryt
                </Button>
              </div>
            </div>
          ) : (
            <>
              {target.adaptedBody ? (
                <div className="border-hairline bg-base/40 mt-3 rounded-lg border p-3">
                  <MarkdownView markdown={target.adaptedBody} />
                </div>
              ) : (
                <p className="text-ink-soft mt-2 text-xs">
                  Ingen kanaltilpasset versjon ennå — Nova klargjør denne når saken planlegges.
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {item.status !== 'PUBLISHED' && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditing(target.id);
                      setDraft(target.adaptedBody ?? item.body ?? '');
                    }}
                  >
                    Rediger kanalversjon
                  </Button>
                )}
                {target.adaptedBody && (
                  <Button
                    size="sm"
                    onClick={() => void navigator.clipboard.writeText(target.adaptedBody ?? '')}
                  >
                    Kopier tekst
                  </Button>
                )}
                {target.status !== 'PUBLISHED_CONFIRMED' && item.status === 'SCHEDULED' && (
                  <div className="flex w-full flex-wrap items-center gap-2 pt-1">
                    <input
                      value={externalRef}
                      onChange={(e) => setExternalRef(e.target.value)}
                      placeholder="Lim inn lenken til publisert innhold"
                      className="border-hairline bg-base/50 text-ink placeholder:text-ink-soft/50 h-8 min-w-0 flex-1 rounded-lg border px-2.5 text-xs"
                    />
                    <Button
                      size="sm"
                      variant="primary"
                      icon={<CheckCircle2 size={13} aria-hidden />}
                      loading={confirm.isPending}
                      onClick={() =>
                        confirm.mutate({
                          channelTargetId: target.id,
                          externalRef: externalRef || null,
                        })
                      }
                    >
                      Bekreft publisert
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      ))}
    </div>
  );
}

// ── Versjonshistorikk ───────────────────────────────────────────────────────

/**
 * The «Versjon N» label above the editor, now a button: it opens a popover with
 * the full version history ({@link HistoryTab}), where each version's body can be
 * expanded. Escape and a click outside close it; it is fully keyboard-reachable.
 */
function VersionHistoryButton({ item }: { item: ContentItemDto }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onOutside(e: Event) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onOutside);
    };
  }, [open]);

  if (!item.versionCount) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="text-ink-soft hover:text-accent inline-flex items-center gap-1 rounded text-[11px] transition"
      >
        <History size={12} aria-hidden />
        Versjon {item.versionCount}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Versjonshistorikk"
          className="border-hairline bg-panel shadow-panel absolute right-0 z-20 mt-1.5 max-h-[22rem] w-[22rem] max-w-[85vw] overflow-y-auto rounded-lg border p-3"
        >
          <p className="label-micro mb-2.5 inline-flex items-center gap-1.5">
            <History size={12} aria-hidden />
            Versjoner
          </p>
          <HistoryTab item={item} />
        </div>
      )}
    </div>
  );
}

function HistoryTab({ item }: { item: ContentItemDto }) {
  const { data: versions = [], isLoading } = useContentVersions(item.id);
  const [openVersion, setOpenVersion] = useState<string | null>(null);

  if (isLoading) return <p className="text-ink-soft text-sm">Henter versjoner …</p>;
  if (versions.length === 0) {
    return (
      <EmptyState
        title="Ingen versjoner ennå"
        description="Historikken fylles så snart teksten lagres første gang."
      />
    );
  }

  return (
    <ol className="space-y-2">
      {versions.map((version) => (
        <li key={version.id} className="border-hairline bg-base/30 rounded-lg border">
          <button
            type="button"
            onClick={() => setOpenVersion(openVersion === version.id ? null : version.id)}
            className="flex w-full items-center justify-between gap-3 px-3.5 py-2.5 text-left"
          >
            <span className="min-w-0">
              <span className="text-ink block text-sm">
                Versjon {version.version} · {version.changedByName}
              </span>
              <span className="text-ink-soft block truncate text-[11px]">
                {formatDateTime(version.createdAt)}
                {version.changeNote && ` · ${version.changeNote}`}
              </span>
            </span>
            {version.changedByType === 'AGENT' && <AgentMark />}
          </button>
          {openVersion === version.id && (
            <div className="border-hairline border-t p-3.5">
              <MarkdownView markdown={version.body} />
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

// ── Handlinger ──────────────────────────────────────────────────────────────

function ActionBar({ item, onClose }: { item: ContentItemDto; onClose: () => void }) {
  const approve = useApproveContent(item.id);
  const reject = useRejectContent(item.id);
  const schedule = useScheduleContent(item.id);
  const confirm = useConfirmPublished(item.id);
  const cancel = useCancelContent(item.id);
  const reopen = useReopenContent(item.id);
  const writeDraft = useWriteDraft(item.id);
  const revert = useRevertPublished(item.id);
  const del = useDeleteContent(item.id);
  const { data: channels = [] } = useChannels();

  const [mode, setMode] = useState<'idle' | 'reject' | 'schedule' | 'undo' | 'rewrite'>('idle');
  const [reason, setReason] = useState('');
  const [instruction, setInstruction] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [publishAt, setPublishAt] = useState(toLocalInput(item.publishAt));
  const [selectedChannels, setSelectedChannels] = useState<string[]>(
    item.channelTargets?.map((t) => t.channelId) ?? [],
  );

  const busy =
    approve.isPending ||
    reject.isPending ||
    schedule.isPending ||
    confirm.isPending ||
    cancel.isPending ||
    reopen.isPending ||
    writeDraft.isPending;

  const error = (approve.error ??
    reject.error ??
    schedule.error ??
    confirm.error ??
    cancel.error ??
    writeDraft.error) as Error | undefined;

  // A 422 from scheduling is the desktest holding the item back — show its
  // reasons and let the owner schedule anyway, rather than a bare error line.
  const scheduleError = schedule.error as ApiError | undefined;
  const review =
    scheduleError?.status === 422
      ? (scheduleError.details as { review?: { issues: string[]; summary: string } } | undefined)
          ?.review
      : undefined;

  function submitSchedule(override = false) {
    schedule.mutate(
      { publishAt: new Date(publishAt).toISOString(), channelIds: selectedChannels, override },
      { onSuccess: () => setMode('idle') },
    );
  }

  if (mode === 'rewrite') {
    return (
      <div className="space-y-2">
        <label htmlFor="rewrite-instruction" className="label-micro block">
          Hva vil du at Nova skal endre? (valgfritt)
        </label>
        <textarea
          id="rewrite-instruction"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={2}
          autoFocus
          placeholder="F.eks. «behold strukturen, men gjør tonen mer ydmyk og mindre bastant»"
          className="border-hairline bg-base/50 text-ink placeholder:text-ink-soft/50 w-full resize-none rounded-lg border p-2.5 text-sm"
        />
        <p className="text-ink-soft text-[11px]">
          Nova tar utgangspunkt i teksten som ligger på saken nå og forbedrer den, med hele profilen
          (tone, posisjonering og eksempler) som grunnlag. La feltet stå tomt for en generell
          forbedring.
        </p>
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            icon={<Sparkles size={14} aria-hidden />}
            loading={writeDraft.isPending}
            onClick={() =>
              writeDraft.mutate(
                { instruction: instruction.trim() || undefined },
                {
                  onSuccess: () => {
                    setInstruction('');
                    setMode('idle');
                  },
                },
              )
            }
          >
            {writeDraft.isPending ? 'Nova skriver …' : 'Skriv på nytt'}
          </Button>
          <Button size="sm" onClick={() => setMode('idle')}>
            Avbryt
          </Button>
        </div>
        {writeDraft.isError && (
          <p className="text-danger text-xs">{(writeDraft.error as Error).message}</p>
        )}
      </div>
    );
  }

  if (mode === 'undo') {
    const phases = ['SCHEDULED', 'DRAFT', 'SUGGESTED'] as const;
    return (
      <div className="space-y-3">
        <div className="border-warning/30 bg-warning/[0.09] rounded-lg border p-3">
          <p className="label-micro text-warning mb-1">Angre publisering</p>
          <p className="text-ink-soft text-sm leading-relaxed">
            Ble det faktisk publisert noe manuelt i denne endringen? Publiseringen i Nova er bare en
            bekreftelse — om noe gikk ut på ekte, bør saken stå som publisert. Angre kun hvis dette
            var en feil og ingenting faktisk ble publisert.
          </p>
        </div>

        <div>
          <p className="label-micro mb-1.5">Flytt tilbake til</p>
          <div className="flex flex-wrap gap-1.5">
            {phases.map((s) => (
              <button
                key={s}
                type="button"
                disabled={revert.isPending || del.isPending}
                onClick={() => revert.mutate(s, { onSuccess: () => setMode('idle') })}
                className="border-hairline bg-raised/50 text-ink-soft hover:text-ink rounded-full border px-2.5 py-1 text-[11px] transition disabled:opacity-50"
              >
                {CONTENT_STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        <div className="border-hairline border-t pt-3">
          {confirmDelete ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-danger text-xs">Fjerne saken helt? Dette kan ikke angres.</span>
              <Button
                variant="danger"
                size="sm"
                loading={del.isPending}
                onClick={() => del.mutate(undefined, { onSuccess: onClose })}
              >
                Ja, fjern
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                Avbryt
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 size={14} aria-hidden />}
              onClick={() => setConfirmDelete(true)}
            >
              Fjern saken helt
            </Button>
          )}
        </div>

        <Button
          size="sm"
          onClick={() => {
            setConfirmDelete(false);
            setMode('idle');
          }}
        >
          Lukk
        </Button>
        {(revert.isError || del.isError) && (
          <p className="text-danger text-xs">{((revert.error ?? del.error) as Error).message}</p>
        )}
      </div>
    );
  }

  if (mode === 'reject') {
    return (
      <div className="space-y-2">
        <label htmlFor="reject-reason" className="label-micro block">
          Hvorfor avvises forslaget?
        </label>
        <textarea
          id="reject-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          autoFocus
          placeholder="Nova bruker begrunnelsen til å lage bedre forslag neste gang."
          className="border-hairline bg-base/50 text-ink placeholder:text-ink-soft/50 w-full resize-none rounded-lg border p-2.5 text-sm"
        />
        <div className="flex gap-2">
          <Button
            variant="danger"
            size="sm"
            disabled={reason.trim().length < 3}
            loading={reject.isPending}
            onClick={() => reject.mutate(reason, { onSuccess: () => setMode('idle') })}
          >
            Avvis forslaget
          </Button>
          <Button size="sm" onClick={() => setMode('idle')}>
            Avbryt
          </Button>
        </div>
      </div>
    );
  }

  if (mode === 'schedule') {
    return (
      <div className="space-y-3">
        <div>
          <label htmlFor="publish-at" className="label-micro mb-1 block">
            Publiseringstidspunkt
          </label>
          <input
            id="publish-at"
            type="datetime-local"
            value={publishAt}
            onChange={(e) => setPublishAt(e.target.value)}
            className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
          />
        </div>
        <div>
          <p className="label-micro mb-1.5">Kanaler</p>
          <div className="flex flex-wrap gap-1.5">
            {channels.map((channel) => {
              const active = selectedChannels.includes(channel.id);
              return (
                <button
                  key={channel.id}
                  type="button"
                  onClick={() =>
                    setSelectedChannels((current) =>
                      active ? current.filter((id) => id !== channel.id) : [...current, channel.id],
                    )
                  }
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-[11px] transition',
                    active
                      ? 'border-accent/40 bg-accent/15 text-accent'
                      : 'border-hairline bg-raised/50 text-ink-soft hover:text-ink',
                  )}
                >
                  {channel.name}
                </button>
              );
            })}
          </div>
        </div>
        {review && (
          <section className="border-warning/30 bg-warning/[0.09] rounded-lg border p-3">
            <p className="label-micro text-warning mb-1.5">Desktesten stoppet planleggingen</p>
            <p className="text-ink-soft mb-2 text-xs">{review.summary}</p>
            {review.issues.length > 0 && (
              <ul className="text-ink-soft list-disc space-y-1 pl-4 text-sm">
                {review.issues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            )}
            <p className="text-ink-soft/70 mt-2 text-[11px]">
              Rett innholdet i «Innhold»-fanen, eller planlegg likevel om du mener det er klart.
            </p>
          </section>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={!publishAt || selectedChannels.length === 0}
            loading={schedule.isPending}
            onClick={() => submitSchedule(false)}
          >
            {review ? 'Kjør desktest på nytt' : 'Planlegg publisering'}
          </Button>
          {review && (
            <Button
              size="sm"
              disabled={!publishAt || selectedChannels.length === 0}
              loading={schedule.isPending}
              onClick={() => submitSchedule(true)}
            >
              Planlegg likevel
            </Button>
          )}
          <Button size="sm" onClick={() => setMode('idle')}>
            Avbryt
          </Button>
        </div>
        {error && !review && <p className="text-danger text-xs">{error.message}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canTransition(item.status, 'APPROVED') && item.status === 'SUGGESTED' && (
        <Button
          variant="primary"
          size="sm"
          icon={<ThumbsUp size={14} aria-hidden />}
          loading={approve.isPending}
          disabled={busy}
          onClick={() => approve.mutate(undefined)}
        >
          Bestill innhold
        </Button>
      )}

      {/* Nova writes the draft right here (synchronously) — a draft is easier to
          take a position on than a title (07 — 2.1.5). No agent, no notifications.
          With text already on the card, «på nytt» opens a steerable revision. */}
      {!['PUBLISHED', 'REJECTED', 'CANCELLED'].includes(item.status) && (
        <Button
          size="sm"
          icon={<Sparkles size={14} aria-hidden />}
          loading={writeDraft.isPending}
          disabled={busy}
          onClick={() => (item.body?.trim() ? setMode('rewrite') : writeDraft.mutate(undefined))}
        >
          {writeDraft.isPending
            ? 'Nova skriver …'
            : item.body?.trim()
              ? 'Be Nova skrive på nytt'
              : 'Be Nova skrive utkast'}
        </Button>
      )}

      {canTransition(item.status, 'REJECTED') && (
        <Button
          size="sm"
          icon={<ThumbsDown size={14} aria-hidden />}
          disabled={busy}
          onClick={() => setMode('reject')}
        >
          Avvis
        </Button>
      )}

      {canTransition(item.status, 'SCHEDULED') && (
        <Button
          variant="primary"
          size="sm"
          icon={<Send size={14} aria-hidden />}
          disabled={busy}
          onClick={() => setMode('schedule')}
        >
          Planlegg
        </Button>
      )}

      {canTransition(item.status, 'PUBLISHED') && (
        <Button
          variant="primary"
          size="sm"
          icon={<CheckCircle2 size={14} aria-hidden />}
          loading={confirm.isPending}
          disabled={busy}
          onClick={() => confirm.mutate({})}
        >
          Bekreft publisert
        </Button>
      )}

      {canTransition(item.status, 'SUGGESTED') && (
        <Button
          size="sm"
          icon={<RotateCcw size={14} aria-hidden />}
          loading={reopen.isPending}
          disabled={busy}
          onClick={() => reopen.mutate(undefined)}
        >
          Tilbake til start
        </Button>
      )}

      {canTransition(item.status, 'CANCELLED') && (
        <Button
          variant="ghost"
          size="sm"
          icon={<Ban size={14} aria-hidden />}
          loading={cancel.isPending}
          disabled={busy}
          onClick={() => cancel.mutate(undefined, { onSuccess: onClose })}
        >
          Kansellér
        </Button>
      )}

      {/* PUBLISHED is otherwise a dead end — this is the way back from a mistaken
          «Bekreft publisert» (menneskelig feil). */}
      {item.status === 'PUBLISHED' && (
        <Button
          variant="ghost"
          size="sm"
          icon={<RotateCcw size={14} aria-hidden />}
          disabled={busy}
          onClick={() => setMode('undo')}
        >
          Angre publisering
        </Button>
      )}

      {error && <p className="text-danger w-full text-xs">{error.message}</p>}
    </div>
  );
}

/** ISO → value accepted by <input type="datetime-local">, in local time. */
function toLocalInput(iso: string | null): string {
  const date = iso ? new Date(iso) : new Date(Date.now() + 86_400_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** ISO → value accepted by <input type="date">, in local time. Empty when unset. */
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
